import type { ReactNode } from 'react';
import { api } from '../api';
import { floorFlow, nextWork, FLOW, SIDE_FLOW } from '../flow';
import { useSession } from '../session';
import type { FloorSummary } from '../types';
import { useLoad } from './index';
import { Icon, type IconName } from './icons';

/** The big button's way: a screen of the app. */
export interface StepLink {
  readonly label: string;
  /** a route ("/paketleme") */
  readonly to: string;
  readonly icon?: IconName;
  /** said right above the button: "Sırada: 5 sipariş paketlenmeyi bekliyor" */
  readonly why?: string;
}

export interface NextStepProps {
  /** what was done, or what comes now, in big letters */
  readonly title: ReactNode;
  /** what the hands do now; numbered when more than one */
  readonly steps?: readonly ReactNode[];
  /** the one big button; null: none (yet) */
  readonly go?: StepLink | null;
  /** ok: a task finished; warn: finished, with something to set aside first */
  readonly tone?: 'next' | 'ok' | 'warn';
  /** the small line on top */
  readonly kicker?: string;
  /** the big button's place is kept while the next work is looked up */
  readonly pending?: boolean;
  /** smaller buttons under the big one (geri al, etiketi yeniden bas, a scan field) */
  readonly children?: ReactNode;
}

/**
 * The end of a floor task, the same on every screen: what was done or comes now, what to do with the
 * hands, and one big button to the next screen, so no screen ends in a dead end. Phone first: the
 * full width, a 56 px button.
 */
export function NextStep({ title, steps = [], go, tone = 'next', kicker = 'Sıradaki adım', pending = false, children }: NextStepProps) {
  return (
    <section className={`card next-step ${tone}`} aria-label={kicker}>
      <span className="next-kicker">
        <Icon name={tone === 'ok' ? 'check' : tone === 'warn' ? 'alert' : 'arrow'} size={16} />
        {kicker}
      </span>
      <div className="next-title">{title}</div>
      {steps.length > 1 ? (
        <ol className="next-steps">
          {steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      ) : (
        steps.length === 1 && <p className="next-steps">{steps[0]}</p>
      )}
      {go?.why && <p className="next-why">{go.why}</p>}
      {go ? (
        <a className="btn primary next-go" href={`#${go.to}`}>
          {go.icon && <Icon name={go.icon} />}
          <span>{go.label}</span>
          <Icon name="arrow" />
        </a>
      ) : (
        pending && (
          <button type="button" className="btn next-go" disabled>
            Sıradaki iş bakılıyor…
          </button>
        )
      )}
      {children && <div className="next-more">{children}</div>}
    </section>
  );
}

/** The floor's counts for a card at a task's end: fresh when it shows (the menu's are up to 30 s old). */
export function useFloorSummary() {
  const { warehouseId } = useSession();
  return useLoad(() => api<FloorSummary>('GET', `/api/wms/summary${warehouseId ? `?warehouseId=${warehouseId}` : ''}`), [warehouseId], 15_000);
}

const HOME: StepLink = { label: 'Özete dön', to: '/', icon: 'home', why: 'Bekleyen başka iş yok.' };
// the counts did not come: no word about the floor, the Özet page shows it
const UNKNOWN: StepLink = { label: 'Özete dön', to: '/', icon: 'home', why: 'Sıradaki iş Özet’te görünür.' };

export interface NextWorkStepProps extends Omit<NextStepProps, 'go' | 'pending'> {
  /** the screen the card is on: its own work is never the next one offered */
  readonly from: string;
  /** while more of this screen's own work waits, this button comes first ("Sıradaki sipariş") */
  readonly more?: StepLink;
}

/**
 * A NextStep whose button leads to the floor's next work by the Özet page's rule (flow.ts), or back
 * to the Özet page when nothing waits.
 */
export function NextWorkStep({ from, more, ...card }: NextWorkStepProps) {
  const summary = useFloorSummary();
  const s = summary.data;
  let go: StepLink | null = summary.error ? UNKNOWN : null;
  if (s) {
    const here = floorFlow(s, [...FLOW, ...SIDE_FLOW]).find((at) => at.path === from && at.waiting > 0);
    const next = nextWork(s, from);
    go = more && here ? { ...more, why: `Sırada: ${here.text}` } : next ? { label: next.action, to: next.path, icon: next.icon, why: `Sırada: ${next.what}` } : HOME;
  }
  return <NextStep {...card} go={go} pending={!go} />;
}
